import React , { Fragment } from "react";
import { Box, Typography, Divider } from "@mui/material";

const ROW_COLORS = (isDark) => [
  isDark ? "#8b5cf6" : "#7c3aed",
  isDark ? "#3b82f6" : "#2563eb",
  isDark ? "#4ade80" : "#16a34a",
  isDark ? "#fbbf24" : "#d97706"
];

const formatVal = (v) => {
  if (v === null || v === undefined || v === "-") return null;
  if (!isNaN(Number(v))) return Number(v).toLocaleString();
  return String(v);
};

const getColLabel = (label) => {
  if (label === "Games per Event") return "Per Event";
  return label;
};

const GAMES_COL_IDX = 2;

export default function StatTable({ title, rows, theme }) {
  if (!rows || rows.length === 0) return null;

  const isDark = theme.palette.mode === "dark";
  const headerRow = rows.find(r => r.header);
  const dataRows = rows.filter(r => !r.header);
  const colLabels = headerRow?.cols.slice(1) || [];
  const colors = ROW_COLORS(isDark);

  const totalGames = dataRows.reduce((sum, row) => {
    const v = row.cols[GAMES_COL_IDX];
    return sum + ((v !== "-" && v !== null && !isNaN(Number(v))) ? Number(v) : 0);
  }, 0);

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        borderRadius: 1,
        p: 2.5,
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
        height: "100%"
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2.5,
          color: theme.palette.text.secondary,
          textAlign: "center"
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {dataRows.map((row, rowIndex) => {
          const color = colors[rowIndex % colors.length];
          const isLast = rowIndex === dataRows.length - 1;
          const gamesVal = row.cols[GAMES_COL_IDX];
          const gamesNum = (gamesVal !== "-" && !isNaN(Number(gamesVal))) ? Number(gamesVal) : 0;
          const pct = totalGames > 0 ? (gamesNum / totalGames) * 100 : 0;

          return (
            <Fragment key={rowIndex}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  py: 1.75
                }}
              >
                <Box
                  sx={{
                    width: 3,
                    borderRadius: 2,
                    backgroundColor: color,
                    flexShrink: 0,
                    mr: 2,
                    my: 0.25
                  }}
                />

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      mb: 0.75
                    }}
                  >
                    {row.cols[0]}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.25 }}>
                    {colLabels.map((label, colIdx) => {
                      const val = formatVal(row.cols[colIdx + 1]);
                      if (val === null) return null;

                      const isGamesCol = colIdx === 1;

                      return (
                        <Box
                          key={colIdx}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5
                          }}
                        >
                          <Typography
                            sx={{
                              color: isGamesCol ? color : theme.palette.text.primary,
                              fontWeight: 700,
                              fontSize: isGamesCol ? "1rem" : "0.875rem",
                              lineHeight: 1.2,
                              fontVariantNumeric: "tabular-nums"
                            }}
                          >
                            {val}
                          </Typography>
                          <Typography
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 400,
                              fontSize: "0.7rem",
                              lineHeight: 1.2
                            }}
                          >
                            {getColLabel(label)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)",
                        overflow: "hidden"
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${pct}%`,
                          borderRadius: 4,
                          background: `linear-gradient(90deg, ${color}55, ${color})`
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        color,
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        minWidth: 42,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0
                      }}
                    >
                      {pct > 0 ? `${pct.toFixed(1)}%` : "–"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {!isLast && (
                <Divider
                  sx={{
                    borderColor: isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.06)"
                  }}
                />
              )}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
